/**
 * AdminHourlyMeters — admin view of every hourly sub's production, split into
 * two cards side by side: TODAY (pay value of today's jobs vs the day rate) and
 * THIS WEEK (pay value of the week's jobs vs the accumulated weekly salary).
 *
 * Same bar logic in both: red→amber below salary, green once covered; the tick
 * marks the salary, the right edge is the goal (salary + $50/day buffer).
 * Refreshes every 45s.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api';

// Red → amber climbing to the salary; green once it's covered.
function meterColor(production, wage) {
  if (wage > 0 && production >= wage) return '#16a34a';
  const f = wage > 0 ? Math.max(0, Math.min(1, production / wage)) : 0;
  return `hsl(${Math.round(45 * f)}, 82%, 47%)`;
}

const fmtMoney = (n) => `$${Math.round((n || 0))}`;

// One card = one period's meter list.
function MeterCard({ title, subtitle, workers }) {
  const list    = Array.isArray(workers) ? workers : [];
  const covered = list.filter(w => w.wage > 0 && w.production >= w.wage).length;

  return (
    <div className="v2-card-lg">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: '#0F2233' }}>{title}</div>
        <span style={{
          width: 7, height: 7, borderRadius: '50%', background: '#16a34a',
          boxShadow: '0 0 0 0 rgba(22,163,74,0.6)', animation: 'meterPulse 1.8s infinite',
        }} />
        <span style={{ marginLeft: 'auto', fontSize: 11.5, color: '#94A3B8', fontWeight: 600 }}>
          {covered}/{list.length} covered
        </span>
      </div>
      <div style={{ fontSize: 11.5, color: '#94A3B8', marginBottom: 12 }}>{subtitle}</div>

      {list.length === 0 ? (
        <div style={{ padding: '20px 0', textAlign: 'center', color: '#cbd5e1', fontSize: 12 }}>Loading…</div>
      ) : (
        <div>
          {list.map((w, i) => {
            const pct      = w.goal > 0 ? Math.round((w.production / w.goal) * 100) : 0;
            const barFrac  = Math.min(w.goal > 0 ? w.production / w.goal : 0, 1);
            const wageMark = Math.min(w.goal > 0 ? w.wage / w.goal : 0, 1) * 100;
            const hitWage  = w.wage > 0 && w.production >= w.wage;
            const hitGoal  = w.goal > 0 && w.production >= w.goal;
            const statusColor = hitGoal ? '#15803d' : hitWage ? '#16a34a' : '#b45309';
            return (
              <div key={w.id} style={{
                padding: '9px 0',
                borderBottom: i < list.length - 1 ? '1px solid #F1F5F9' : 'none',
              }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 5 }}>
                  <div style={{
                    flex: 1, minWidth: 0, fontSize: 12.5, fontWeight: 700, color: '#0F2233',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }} title={w.name}>
                    {hitGoal ? '🎉 ' : ''}{w.name}
                  </div>
                  <div style={{ fontSize: 12.5, fontWeight: 800, color: statusColor, whiteSpace: 'nowrap' }}>
                    {pct}%
                  </div>
                  <div style={{ fontSize: 10.5, color: '#64748B', fontWeight: 600, whiteSpace: 'nowrap', minWidth: 84, textAlign: 'right' }}>
                    {fmtMoney(w.production)} / {fmtMoney(w.goal)}
                    {w.jobCount > 0 && <> · {w.jobCount}j</>}
                  </div>
                </div>
                <div style={{ position: 'relative', height: 11, background: '#F1F5F9', borderRadius: 999, overflow: 'hidden' }}>
                  <div style={{
                    position: 'absolute', left: 0, top: 0, bottom: 0,
                    width: `${barFrac * 100}%`,
                    background: meterColor(w.production, w.wage),
                    borderRadius: 999,
                    transition: 'width 0.5s ease, background 0.5s ease',
                  }} />
                  <div style={{
                    position: 'absolute', top: -1, bottom: -1, left: `${wageMark}%`,
                    width: 2, background: hitWage ? '#15803d' : '#475569',
                  }} title={`Salary ${fmtMoney(w.wage)}`} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function AdminHourlyMeters() {
  const [daily, setDaily]   = useState(null);
  const [weekly, setWeekly] = useState(null);

  const load = useCallback(() => {
    api.getHourlyProductionAll()
      .then(r => setDaily(Array.isArray(r.workers) ? r.workers : []))
      .catch(() => {});
    api.getHourlyProductionWeek()
      .then(r => setWeekly(Array.isArray(r.workers) ? r.workers : []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 45_000);
    return () => clearInterval(id);
  }, [load]);

  // Nothing to show until we know there are hourly workers.
  if (!daily || daily.length === 0) return null;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16, marginBottom: 16 }}>
      <MeterCard
        title="📊 Today"
        subtitle="Pay value of each sub's jobs today vs their day rate"
        workers={daily}
      />
      <MeterCard
        title="📅 This Week"
        subtitle="Pay value of each sub's jobs this week vs their weekly salary"
        workers={weekly}
      />
      <style>{`@keyframes meterPulse {
        0%   { box-shadow: 0 0 0 0 rgba(22,163,74,0.55); }
        70%  { box-shadow: 0 0 0 7px rgba(22,163,74,0); }
        100% { box-shadow: 0 0 0 0 rgba(22,163,74,0); }
      }`}</style>
    </div>
  );
}
